import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import VideoComponent from "./VideoComponent";
import { Card, CardBody, Container } from "reactstrap";
import MPSBarLoader from "../MPSBarLoader";
import MPSButton from "../Buttons/Buttons/MPSButton";
import moment from "moment";
import isIOSChromeOrSafari, { doPollingToJoinCall } from "./Utils";
import CallWaitTimer from "./CallWaitTimer";
import userService from "../../services/userService";
import GoToHome from "./GoToHome";
import UserIcon from "./UserIcon";
import PatientWebexMeeting from "./WebEx/Patient/PatientWebexMeeting";

import I18nProvider from "../../services/I18nProvider";
import Text from "../../components/Text";
import Messages from "../../Shared/Messages";
import { applyThemeColor } from "../../utils/themeColor";
import { isStaff } from "../../security/roles";

const intl = I18nProvider.getIntlProvider();

const ERROR =
  "Unable to join the video call. May be the call is not scheduled yet. Please contact your health care provider.";

const cardStyle = {
  boxShadow: "0px 2px 8px rgba(0,0,0,0.5)",
  borderRadius: "6px",
  border: 0,
};

function getOtherParticipantName(participants, userId) {
  let filtered = participants.filter((p) => p.userId !== userId);
  if (filtered && filtered[0]) {
    return filtered[0].displayHumanName.text;
  }
  return null;
}

class ExistingRoomContainer extends Component {
  constructor(props) {
    super();
    this.state = {};
  }

  componentDidMount() {
    // This route renders outside the Full shell, so the facility theme CSS
    // vars (--theme*) are not set yet — publish them from the session here.
    applyThemeColor(this.props.appTheme && this.props.appTheme.themeColor);

    let { roomName } = this.props;
    this.setState({ loading: true });
    userService
      .fetchVideoRoom(roomName)
      .then((videoRoom) => {
        // Staff join scheduled calls in the in-app mini viewer on the
        // patient's profile, not in this full-page view. The floating video
        // call provider picks up the joinVideoRoom query param.
        if (isStaff(this.props.userRole) && videoRoom.provider !== "WEBEX") {
          const patientRef = (videoRoom.participants || []).find(
            (p) => p.patientId
          );
          if (patientRef && this.props.history) {
            this.props.history.replace(
              `/patients/${patientRef.patientId}?joinVideoRoom=${roomName}`
            );
            return;
          }
        }

        let { createdBy } = videoRoom;
        let isHost = createdBy.userId === this.props.userId;
        this.setState({ videoRoom, isHost, loading: false });

        if (videoRoom.provider === "WEBEX") {
          return;
        }

        let scheduledStart = videoRoom.scheduledStart;
        let scheduledEnd = videoRoom.scheduledEnd;

        if (scheduledEnd) {
          let diffInMin = moment().diff(moment(scheduledEnd), "minute");

          if (diffInMin >= 0) {
            this.setState({
              showCallButton: false,
              callEnded: true,
            });
            return;
          }
        }

        let showCallButton = !videoRoom.scheduledStart;
        if (scheduledStart) {
          let diffInMin = moment(videoRoom.scheduledStart).diff(
            moment(),
            "minute"
          );
          if (diffInMin <= 1) {
            showCallButton = true;
          } else {
            setTimeout(() => {
              this.setState({ showCallButton: true });
            }, diffInMin * 60 * 1000);
          }
        }
        this.setState({ showCallButton });
      })
      .catch(this.handleError);
  }

  onStartAnonymousCall = (e) => {
    let { roomName } = this.props;
    this.setState({ loading: true });

    doPollingToJoinCall(
      roomName,
      (data) => {
        this.setState(data);
      },
      moment(),
      this.handleError
    );
  };

  handleError = (error) => {
    this.setState({
      loading: false,
      error: error.message || ERROR,
    });
  };

  render() {
    let {
      loading,
      token,
      error,
      participants,
      callNotReady,
      videoRoom,
      showCallButton,
      callEnded,
    } = this.state;

    let { userName, roomName } = this.props;

    if (loading) {
      return (
        <CallView>
          <div className="loading-section">
            <div className="pulse-ring"></div>
            <MPSBarLoader />
            <p className="loading-text">Connecting to your call...</p>
          </div>
        </CallView>
      );
    }

    if (error) {
      return (
        <Fragment>
          <CallView>
            <div className="error-section">
              <div className="user-icon-container">
                <UserIcon />
              </div>
              <div className="error-content">
                <div className="error-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#ef4444"
                      strokeWidth="2"
                    />
                    <line
                      x1="15"
                      y1="9"
                      x2="9"
                      y2="15"
                      stroke="#ef4444"
                      strokeWidth="2"
                    />
                    <line
                      x1="9"
                      y1="9"
                      x2="15"
                      y2="15"
                      stroke="#ef4444"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <h3 className="error-title">Connection Failed</h3>
                <p className="error-message">{error}</p>
              </div>
            </div>
          </CallView>
          <div className="home-button-container">
            <GoToHome />
          </div>
        </Fragment>
      );
    }

    if (callNotReady) {
      return (
        <CallView>
          <div className="waiting-section">
            <div className="user-icon-container">
              <UserIcon />
            </div>
            <div className="waiting-content">
              <div className="waiting-animation">
                <div className="waiting-dots">
                  <div className="dot"></div>
                  <div className="dot"></div>
                  <div className="dot"></div>
                </div>
              </div>
              <h3 className="waiting-title">Waiting for Connection</h3>
              <p className="waiting-message">
                Call with{" "}
                <span className="participant-name">
                  {getOtherParticipantName(participants, this.props.userId)}
                </span>
                , waiting for them to join.
              </p>
              <div className="timer-container">
                <CallWaitTimer />
              </div>
            </div>
          </div>
        </CallView>
      );
    }

    if (callEnded) {
      return (
        <Fragment>
          <CallView>
            <div className="call-ended-section">
              {userName && (
                <div className="greeting">
                  Hello <span className="user-name">{userName}</span>
                </div>
              )}
              <div className="ended-content">
                <div className="ended-icon">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#6b7280"
                      strokeWidth="2"
                    />
                    <polyline
                      points="9,11 12,14 22,4"
                      stroke="#6b7280"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <h3 className="ended-title">Call Completed</h3>
                <p className="ended-message">This call has already ended.</p>
              </div>
            </div>
          </CallView>
          <div className="home-button-container">
            <GoToHome />
          </div>
        </Fragment>
      );
    }

    if (token) {
      return (
        <div className="video-call-active">
          <div className="video-container">
            <VideoComponent
              token={token}
              roomName={roomName}
              participants={participants}
              otherParticipantName={getOtherParticipantName(
                participants,
                this.props.userId
              )}
              existing={true}
              userRole={this.props.userRole}
            />
          </div>
        </div>
      );
    }

    if (videoRoom) {
      if (videoRoom.provider === "WEBEX") {
        return (
          <PatientWebexMeeting
            sipAddress={roomName}
            roomNameAndUserId={roomName}
            auth
            isHost={this.state.isHost}
          />
        );
      }

      if (showCallButton) {
        return (
          <CallView>
            <div className="join-call-section">
              {userName && (
                <div className="greeting">
                  Hello <span className="user-name">{userName}</span>
                </div>
              )}

              <div className="call-info">
                <div className="video-icon">
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M23 7L16 12L23 17V7Z" fill="url(#gradient1)" />
                    <rect
                      x="1"
                      y="5"
                      width="15"
                      height="14"
                      rx="2"
                      fill="url(#gradient2)"
                    />
                    <defs>
                      <linearGradient
                        id="gradient1"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="var(--theme, #00c2d1)" />
                        <stop
                          offset="100%"
                          stopColor="var(--theme-dark, #017992)"
                        />
                      </linearGradient>
                      <linearGradient
                        id="gradient2"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor="var(--theme-accent, #33b1e6)"
                        />
                        <stop offset="100%" stopColor="var(--theme, #00c2d1)" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <h3 className="call-title">Ready to Join</h3>
                <p className="call-description">
                  Click to join your video call with{" "}
                  <span className="participant-name">
                    {getOtherParticipantName(
                      videoRoom.participants,
                      this.props.userId
                    )}
                  </span>
                </p>
              </div>

              {isIOSChromeOrSafari() ? (
                <div className="browser-warning">
                  <div className="warning-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="#f59e0b"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="warning-content">
                    <h4>Browser Not Supported</h4>
                    <p>
                      Chrome/Firefox on iPhone and iPad are not supported.
                      Please copy the URL from address bar and open it in Safari
                      browser.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="join-button-container">
                  <MPSButton
                    className="join-button"
                    color="green"
                    onClick={this.onStartAnonymousCall}
                    size="lg"
                  >
                    <div className="button-content">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.5 14H20.5L22 15.5V6.5L20.5 5H3.5L2 6.5V15.5L3.5 17H9.5L15.5 14Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M11 8.5V11.5L13 10L11 8.5Z"
                          fill="currentColor"
                        />
                      </svg>
                      <Text content={Messages.join_video_call} />
                    </div>
                  </MPSButton>
                </div>
              )}
            </div>
          </CallView>
        );
      } else {
        return (
          <Fragment>
            <CallView>
              <div className="scheduled-call-section">
                {userName && (
                  <div className="greeting">
                    Hello <span className="user-name">{userName}</span>
                  </div>
                )}

                <div className="schedule-info">
                  <div className="clock-icon">
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="url(#clockGradient)"
                        strokeWidth="2"
                      />
                      <polyline
                        points="12,6 12,12 16,14"
                        stroke="url(#clockGradient)"
                        strokeWidth="2"
                      />
                      <defs>
                        <linearGradient
                          id="clockGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="var(--theme, #00c2d1)" />
                          <stop
                            offset="100%"
                            stopColor="var(--theme-dark, #017992)"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  <h3 className="schedule-title">Call Scheduled</h3>
                  <p className="schedule-description">
                    Video call with{" "}
                    <span className="participant-name">
                      {getOtherParticipantName(
                        videoRoom.participants,
                        this.props.userId
                      )}
                    </span>{" "}
                    has not started yet.
                  </p>
                </div>

                <div className="schedule-details">
                  <div className="schedule-label">
                    Call Scheduled to Start at:
                  </div>

                  <div className="schedule-time">
                    {moment(videoRoom.scheduledStart).format("HH:mm")}
                  </div>

                  <div className="schedule-date">
                    {new Date(videoRoom.scheduledStart).toLocaleDateString()}
                  </div>

                  <div className="auto-refresh-info">
                    <div className="refresh-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 4V10H7M23 20V14H17M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    This page will auto-refresh. A Join Now button will appear
                    when call is open.
                  </div>
                </div>
              </div>
            </CallView>
            <div className="home-button-container">
              <GoToHome />
            </div>
          </Fragment>
        );
      }
    }

    return null;
  }
}

function CallView({ children }) {
  return (
    <div className="call-view-container">
      <div className="call-view-card">{children}</div>

      <style jsx>{`
        .call-view-container {
          min-height: 100vh;
          background: linear-gradient(
            135deg,
            var(--theme, #00c2d1) 0%,
            var(--theme-dark, #017992) 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
        }

        .call-view-container::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.05"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.05"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
        }

        .call-view-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 3rem;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
          max-width: 600px;
          width: 100%;
          text-align: center;
          position: relative;
          z-index: 2;
          opacity: 0;
          animation: fadeInUp 0.8s ease-out 0.2s forwards;
        }

        .loading-section {
          text-align: center;
          position: relative;
          padding: 2rem 0;
        }

        .pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120px;
          height: 120px;
          border: 3px solid
            color-mix(in srgb, var(--theme, #00c2d1) 30%, transparent);
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .loading-text {
          color: #374151;
          font-size: 1.1rem;
          margin-top: 2rem;
          font-weight: 500;
        }

        .error-section {
          text-align: center;
        }

        .user-icon-container {
          margin-bottom: 2rem;
        }

        .error-content {
          text-align: center;
        }

        .error-icon {
          margin-bottom: 1.5rem;
        }

        .error-title {
          color: #ef4444;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .error-message {
          color: #6b7280;
          line-height: 1.6;
          font-size: 1rem;
        }

        .waiting-section {
          text-align: center;
        }

        .waiting-content {
          text-align: center;
        }

        .waiting-animation {
          margin-bottom: 2rem;
        }

        .waiting-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
        }

        .dot {
          width: 12px;
          height: 12px;
          background: var(--theme, #00c2d1);
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .dot:nth-child(1) {
          animation-delay: -0.32s;
        }
        .dot:nth-child(2) {
          animation-delay: -0.16s;
        }

        .waiting-title {
          color: #374151;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .waiting-message {
          color: #6b7280;
          font-size: 1.1rem;
          margin-bottom: 2rem;
          line-height: 1.5;
        }

        .participant-name {
          color: var(--theme-deep, #017992);
          font-weight: 600;
        }

        .timer-container {
          background: #f3f4f6;
          padding: 1rem;
          border-radius: 12px;
          display: inline-block;
        }

        .call-ended-section {
          text-align: center;
        }

        .greeting {
          font-size: 1.5rem;
          color: #374151;
          margin-bottom: 2rem;
          font-weight: 500;
        }

        .user-name {
          color: var(--theme-deep, #017992);
          font-weight: 600;
        }

        .ended-content {
          text-align: center;
        }

        .ended-icon {
          margin-bottom: 1.5rem;
        }

        .ended-title {
          color: #374151;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .ended-message {
          color: #6b7280;
          font-size: 1rem;
        }

        .join-call-section {
          text-align: center;
        }

        .call-info {
          margin-bottom: 2rem;
        }

        .video-icon {
          margin-bottom: 1.5rem;
        }

        .call-title {
          color: #374151;
          font-size: 1.8rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .call-description {
          color: #6b7280;
          font-size: 1.1rem;
          line-height: 1.5;
        }

        .browser-warning {
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          border: 1px solid #f59e0b;
          border-radius: 16px;
          padding: 1.5rem;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          text-align: left;
        }

        .warning-icon {
          flex-shrink: 0;
          margin-top: 0.25rem;
        }

        .warning-content h4 {
          color: #92400e;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .warning-content p {
          color: #78350f;
          margin: 0;
          line-height: 1.5;
        }

        .join-button-container {
          margin-top: 2rem;
        }

        .join-button {
          background: linear-gradient(
            135deg,
            var(--theme, #00c2d1) 0%,
            var(--theme-dark, #017992) 100%
          ) !important;
          border: none !important;
          border-radius: 16px !important;
          padding: 1rem 2.5rem !important;
          font-size: 1.1rem !important;
          font-weight: 600 !important;
          color: white !important;
          box-shadow: 0 10px 25px
            color-mix(in srgb, var(--theme, #00c2d1) 30%, transparent) !important;
          transition: all 0.3s ease !important;
        }

        .join-button:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 15px 30px
            color-mix(in srgb, var(--theme, #00c2d1) 40%, transparent) !important;
          background: linear-gradient(
            135deg,
            var(--theme-dark, #017992) 0%,
            var(--theme-deep, #01576a) 100%
          ) !important;
        }

        .button-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .scheduled-call-section {
          text-align: center;
        }

        .schedule-info {
          margin-bottom: 2rem;
        }

        .clock-icon {
          margin-bottom: 1.5rem;
        }

        .schedule-title {
          color: #374151;
          font-size: 1.8rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .schedule-description {
          color: #6b7280;
          font-size: 1.1rem;
          line-height: 1.5;
        }

        .schedule-details {
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          border-radius: 16px;
          padding: 2rem;
          border: 1px solid #e2e8f0;
        }

        .schedule-label {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 1rem;
          font-weight: 500;
        }

        .schedule-time {
          font-size: 3rem;
          font-weight: 700;
          color: var(--theme-deep, #017992);
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 4px
            color-mix(in srgb, var(--theme, #00c2d1) 10%, transparent);
        }

        .schedule-date {
          font-size: 1.25rem;
          color: #64748b;
          margin-bottom: 2rem;
          font-weight: 500;
        }

        .auto-refresh-info {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: 0.9rem;
          font-style: italic;
        }

        .refresh-icon {
          animation: spin 2s linear infinite;
        }

        .home-button-container {
          margin-top: 2rem;
          text-align: center;
        }

        .video-call-active {
          min-height: 100vh;
          background: #1a1a1a;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .video-container {
          width: 100%;
          max-width: 1200px;
          background: #000;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0;
          }
        }

        @keyframes bounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .call-view-container {
            padding: 1rem;
          }

          .call-view-card {
            padding: 2rem;
            border-radius: 16px;
          }

          .greeting {
            font-size: 1.25rem;
          }

          .call-title,
          .schedule-title {
            font-size: 1.5rem;
          }

          .schedule-time {
            font-size: 2.5rem;
          }

          .schedule-date {
            font-size: 1.1rem;
          }

          .browser-warning {
            flex-direction: column;
            text-align: center;
          }

          .warning-content {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}

const mapstatetoprops = (state, ownProps) => {
  return {
    roomName:
      ownProps.match && ownProps.match.params && ownProps.match.params.roomName
        ? ownProps.match.params.roomName
        : null,
    userId: state.user.data.userId,
    userName: state.user.data.loggedInUser.text || null,
    userRole: state.user.role,
    appTheme: state.user.data.appTheme || {},
  };
};

export default connect(mapstatetoprops)(ExistingRoomContainer);
